import { FieldErrors } from "react-hook-form";

import { isNotNullOrUndefined } from "@smart/itops-utils-basic";

import { fieldName } from "./field";
import {
  FieldError,
  FieldItem,
  GroupError,
  OrderedGroup,
  OrderedItem,
  SectionErrors,
} from "../types";
import { getTranslation } from "../utils/translation";
import { getValidationMessage } from "../utils/ui-translations";

const fieldError = ({
  field,
  error,
  index,
  selectedLanguage,
}: {
  field: FieldItem;
  error: FieldErrors[string];
  index?: number;
  selectedLanguage?: string;
}): FieldError | undefined =>
  error
    ? {
        name: fieldName({ field, index }),
        label:
          getTranslation(field.translations, selectedLanguage)?.label ??
          field.label,
        error: getValidationMessage(
          error?.message?.toString(),
          selectedLanguage,
        ),
      }
    : undefined;

const groupErrors = (
  { group, groupFieldSets }: OrderedGroup,
  errors: FieldErrors,
  selectedLanguage?: string,
): GroupError[] =>
  groupFieldSets
    .map((fieldSet) => {
      const index = fieldSet.index || 0;
      const fieldErrors = fieldSet.visibleFields
        .map((field) =>
          fieldError({
            field,
            error: (errors?.[field.uri] as FieldErrors)?.[index],
            index,
            selectedLanguage,
          }),
        )
        .filter(isNotNullOrUndefined);

      const groupLabel =
        getTranslation(group.translations, selectedLanguage)?.label ??
        group.label;
      return fieldErrors.length
        ? {
            name: fieldName({ field: group, index }),
            fieldErrors,
            index: group.repeatable ? index : undefined,
            label: groupLabel,
          }
        : undefined;
    })
    .filter(isNotNullOrUndefined);

export const useOrderedErrors = (
  { orderedItems }: { orderedItems: OrderedItem[] },
  state: {
    isValid: boolean;
    errors: FieldErrors;
  },
  selectedLanguage?: string,
): SectionErrors | undefined => {
  if (state.isValid) return undefined;

  const errors = orderedItems
    .map((item) =>
      item.type === "field"
        ? fieldError({
            field: item.field,
            error: state.errors[item.field.uri],
            index: undefined,
            selectedLanguage,
          })
        : groupErrors(item, state.errors, selectedLanguage),
    )
    .filter(isNotNullOrUndefined)
    .flat();

  const total = errors.reduce(
    (agg, e) => agg + (e.fieldErrors?.length || 1),
    0,
  );

  return { errors, total };
};
